/**
 * Merchant POS permissions — ใช้กับ shop_staff.staffRole
 * แยกจาก Platform permissions (users, audit)
 */
export const MerchantPermissions = {
  catalog: {
    read: "catalog:read",
    create: "catalog:create",
    update: "catalog:update",
    delete: "catalog:delete",
  },
  inventory: {
    read: "inventory:read",
    adjust: "inventory:adjust",
    receive: "inventory:receive",
    transfer: "inventory:transfer",
  },
  pos: {
    sell: "pos:sell",
    refund: "pos:refund",
    void: "pos:void",
    return: "pos:return",
    read: "pos:read",
  },
  settlement: {
    read: "settlement:read",
    requestPayout: "settlement:request_payout",
  },
  shop_staff: {
    read: "shop_staff:read",
    create: "shop_staff:create",
    update: "shop_staff:update",
    delete: "shop_staff:delete",
  },
  /** Super admin ของร้านจัดการ role เอง */
  shop_roles: {
    read: "shop_roles:read",
    create: "shop_roles:create",
    update: "shop_roles:update",
    delete: "shop_roles:delete",
  },
  shifts: {
    read: "shifts:read",
    manage: "shifts:manage",
    clock: "shifts:clock",
  },
  claims: {
    read: "claims:read",
    create: "claims:create",
    manage: "claims:manage",
  },
  promotions: {
    read: "promotions:read",
    manage: "promotions:manage",
  },
  marketplace: {
    read: "marketplace:read",
    publish: "marketplace:publish",
  },
  workshop: {
    read: "workshop:read",
    create: "workshop:create",
    sell: "workshop:sell",
    manage: "workshop:manage",
  },
} as const;

type NestedValues<T> = T[keyof T] extends Record<string, infer V> ? V : never;
export type MerchantPermissionId = NestedValues<typeof MerchantPermissions>;

export const ALL_MERCHANT_PERMISSIONS: MerchantPermissionId[] = Object.values(
  MerchantPermissions,
).flatMap((a) =>
  Object.values(a as Record<string, string>),
) as MerchantPermissionId[];

/** staffRole จาก shop_staff → ชุด permission ที่ได้ */
export type StaffRole = "owner" | "manager" | "cashier";

const {
  catalog,
  inventory,
  pos,
  settlement,
  shop_staff,
  shop_roles,
  shifts,
  claims,
  promotions,
  marketplace,
  workshop,
} = MerchantPermissions;

/** ใช้เป็น default ตอนสร้าง role ใหม่ของร้าน หรือ fallback เมื่อ staff ยังใช้ staff_role เก่า */
export const STAFF_ROLE_PERMISSIONS: Record<StaffRole, MerchantPermissionId[]> =
  {
    owner: [...ALL_MERCHANT_PERMISSIONS],
    manager: [
      catalog.read,
      catalog.create,
      catalog.update,
      catalog.delete,
      inventory.read,
      inventory.adjust,
      inventory.receive,
      inventory.transfer,
      pos.sell,
      pos.refund,
      pos.void,
      pos.return,
      pos.read,
      settlement.read,
      shop_staff.read,
      shop_staff.create,
      shop_staff.update,
      shop_staff.delete,
      shop_roles.read,
      shifts.read,
      shifts.manage,
      shifts.clock,
      claims.read,
      claims.create,
      claims.manage,
      promotions.read,
      promotions.manage,
      marketplace.read,
      marketplace.publish,
      workshop.read,
      workshop.create,
      workshop.sell,
      workshop.manage,
    ],
    cashier: [
      catalog.read,
      inventory.read,
      pos.sell,
      pos.refund,
      pos.read,
      shifts.read,
      shifts.clock,
      claims.read,
      claims.create,
      /** ช่างหน้าร้าน — ดู/สร้างใบงานได้ ยังไม่ checkout */
      workshop.read,
      workshop.create,
    ],
  };
